import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Ticket, 
  Plus, 
  Trash2,
  Loader2,
  Calendar,
  DollarSign,
  Trophy,
  Check,
  FileText,
  Download,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const day = days[date.getDay()];
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${day} ${dd}.${mm}.${parts[0]}`;
};

const LotteryAccordion = ({ lotteryKey, label, items, deleteTicket, deleteDateGroup }) => {
  const [open, setOpen] = useState(false);
  const totalSpend = items.reduce((s, t) => s + (t.ticket_cost || 0), 0);
  const totalWin = items.reduce((s, t) => s + (t.winnings || 0), 0);
  const winners = items.filter(t => (t.winnings || 0) > 0).length;

  const colorMap = {
    oz_lotto: 'from-blue-500/20 to-blue-900/10 border-blue-500/30',
    tatts_lotto: 'from-purple-500/20 to-purple-900/10 border-purple-500/30',
    weekday_windfall: 'from-teal-500/20 to-teal-900/10 border-teal-500/30',
    powerball: 'from-red-500/20 to-red-900/10 border-red-500/30',
  };

  // Group items by draw_date (descending)
  const dateGroups = items.reduce((acc, t) => {
    const d = t.draw_date || 'unknown';
    if (!acc[d]) acc[d] = [];
    acc[d].push(t);
    return acc;
  }, {});
  const sortedDates = Object.keys(dateGroups).sort((a, b) => b.localeCompare(a));

  return (
    <div className={`rounded-xl border bg-gradient-to-r ${colorMap[lotteryKey] || 'from-white/5 to-transparent border-white/10'} overflow-hidden`} data-testid={`lottery-group-${lotteryKey}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
        data-testid={`lottery-accordion-${lotteryKey}`}
      >
        <div className="flex items-center gap-3">
          <Ticket className="w-5 h-5 text-white/70" />
          <span className="font-heading text-lg font-semibold text-white capitalize">{label}</span>
          <span className="text-sm text-aura-text-muted">({items.length} game{items.length !== 1 ? 's' : ''})</span>
          {winners > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-aura-gold/20 text-aura-gold">{winners} winner{winners !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-aura-text-muted hidden sm:block">
            Spend: ${totalSpend.toFixed(2)} | Win: ${totalWin.toFixed(2)}
          </span>
          <ChevronDown className={`w-5 h-5 text-aura-text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5">
          {sortedDates.map((dateKey, dIdx) => {
            const groupItems = dateGroups[dateKey];
            const groupSpend = groupItems.reduce((s, t) => s + (t.ticket_cost || 0), 0);
            const groupWin = groupItems.reduce((s, t) => s + (t.winnings || 0), 0);
            return (
              <div key={dateKey} data-testid={`date-group-${lotteryKey}-${dateKey}`}>
                {dIdx > 0 && <div className="h-px bg-white/10" />}
                {/* Date Group Header */}
                <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Calendar className="w-3.5 h-3.5 text-aura-text-muted" />
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">{formatDate(dateKey)}</span>
                    <span className="text-xs text-aura-text-muted">{groupItems.length} game{groupItems.length !== 1 ? 's' : ''}</span>
                    <span className="text-xs text-aura-text-muted hidden sm:inline">| Spend ${groupSpend.toFixed(2)} | Win ${groupWin.toFixed(2)}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-aura-text-muted hover:text-red-400 h-7 px-2 text-xs"
                    onClick={() => deleteDateGroup(lotteryKey, dateKey, groupItems)}
                    data-testid={`delete-date-group-${lotteryKey}-${dateKey}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete Group
                  </Button>
                </div>
                {/* Tickets in this group */}
                <div className="divide-y divide-white/5">
                  {groupItems.map((ticket) => {
                    const matchedNums = ticket.matched_numbers || [];
                    const matchedSupps = ticket.matched_supps || [];
                    const hasWin = (ticket.winnings || 0) > 0;
                    const isChecked = ticket.checked_at || ticket.matches !== null;
                    return (
                      <div
                        key={ticket.ticket_id}
                        className={`p-4 ${hasWin ? 'bg-aura-emerald/5' : 'hover:bg-white/5'} transition-colors`}
                        data-testid={`ticket-row-${ticket.ticket_id}`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${hasWin ? 'bg-aura-gold/20' : 'bg-white/5'}`}>
                              {hasWin ? <Trophy className="w-4 h-4 text-aura-gold" /> : <Ticket className="w-4 h-4 text-aura-text-muted" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                {hasWin && <span className="text-xs px-2 py-0.5 rounded-full bg-aura-gold/20 text-aura-gold font-medium">WINNER</span>}
                                {isChecked && !hasWin && <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-aura-text-muted">Checked</span>}
                                {ticket.division && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-aura-emerald/10 text-aura-emerald border border-aura-emerald/20">Div {ticket.division}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-aura-text-muted mt-0.5">
                                <span>${ticket.ticket_cost?.toFixed(2)}</span>
                                {ticket.matches !== null && ticket.matches !== undefined && (
                                  <>
                                    <span>&middot;</span>
                                    <span className={ticket.matches >= 3 ? 'text-aura-emerald' : ''}>{ticket.matches} match{ticket.matches !== 1 ? 'es' : ''}</span>
                                  </>
                                )}
                                {hasWin && (
                                  <>
                                    <span>&middot;</span>
                                    <span className="text-aura-gold font-medium">Won ${ticket.winnings.toFixed(2)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1 flex-wrap">
                              {ticket.numbers?.slice(0, 7).map((num, i) => {
                                const isMatched = matchedNums.includes(num);
                                const isSuppMatch = matchedSupps.includes(num);
                                return (
                                  <span key={i} className={`lottery-ball small ${isMatched ? 'winner' : isSuppMatch ? 'supp-match' : ''}`} data-testid={`ticket-num-${ticket.ticket_id}-${i}`}>{num}</span>
                                );
                              })}
                              {ticket.powerball && (
                                <span className={`lottery-ball powerball small ml-1 ${ticket.powerball_match ? 'winner' : ''}`}>{ticket.powerball}</span>
                              )}
                            </div>
                            <Button variant="ghost" size="icon" className="text-aura-text-muted hover:text-red-400 h-8 w-8" onClick={() => deleteTicket(ticket.ticket_id)} data-testid={`delete-ticket-${ticket.ticket_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const TicketsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [predictionDialogOpen, setPredictionDialogOpen] = useState(false);
  const [lotteryTypes, setLotteryTypes] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    lottery_type: "",
    draw_date: "",
    numbers: "",
    powerball: "",
    ticket_cost: ""
  });
  const [submitting, setSubmitting] = useState(false);

  // Prediction import state
  const [importPredictions, setImportPredictions] = useState(null);
  const [selectedGames, setSelectedGames] = useState([]);
  const [importCost, setImportCost] = useState("1.45");

  // Canonical per-game costs (AUD) — kept in sync with backend GAME_COSTS.
  const GAME_COSTS = {
    tatts_lotto: "0.9625",
    oz_lotto: "1.50",
    powerball: "1.575",
    weekday_windfall: "0.675",
  };

  const isPro = user?.email === "ai.manifest.official@gmail.com" || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";

  useEffect(() => {
    fetchTickets();
    fetchLotteryTypes();
  }, []);

  // Check for predictions passed from Predictions page
  useEffect(() => {
    if (location.state?.predictions) {
      const incomingLottery = location.state.lotteryType;
      setImportPredictions({
        ...location.state.predictions,
        lotteryType: incomingLottery,
        drawDate: location.state.drawDate
      });
      // Pre-fill cost based on lottery type — user can still edit
      if (incomingLottery && GAME_COSTS[incomingLottery]) {
        setImportCost(GAME_COSTS[incomingLottery]);
      }
      setPredictionDialogOpen(true);
      // Clear state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to fetch games");
    } finally {
      setLoading(false);
    }
  };

  const fetchLotteryTypes = async () => {
    try {
      const response = await axios.get(`${API}/lottery-types`);
      setLotteryTypes(response.data.types);
    } catch (error) {
      console.error("Failed to fetch lottery types");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const numbersArray = formData.numbers
      .split(/[\s,]+/)
      .map(n => parseInt(n.trim()))
      .filter(n => !isNaN(n));

    if (numbersArray.length < 6) {
      toast.error("Please enter at least 6 numbers");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/tickets`,
        {
          lottery_type: formData.lottery_type,
          draw_date: formData.draw_date,
          numbers: numbersArray,
          powerball: formData.powerball ? parseInt(formData.powerball) : null,
          ticket_cost: parseFloat(formData.ticket_cost)
        },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success("Game added!");
      setDialogOpen(false);
      setFormData({
        lottery_type: "",
        draw_date: "",
        numbers: "",
        powerball: "",
        ticket_cost: ""
      });
      fetchTickets();
    } catch (error) {
      if (error.response?.data?.detail === "DUPLICATE_TICKET") {
        toast.error("This game has already been added to your Games Tracker.");
      } else {
        toast.error("Failed to add game");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPredictions = async (addAll = false) => {
    if (!importPredictions) return;

    const gamesToAdd = addAll 
      ? importPredictions.numbers.map((_, i) => i)
      : selectedGames;

    if (gamesToAdd.length === 0) {
      toast.error("Please select at least one game");
      return;
    }

    setSubmitting(true);
    let addedCount = 0;
    let duplicateCount = 0;
    
    try {
      for (const gameIdx of gamesToAdd) {
        try {
          await axios.post(
            `${API}/tickets`,
            {
              lottery_type: importPredictions.lotteryType,
              draw_date: importPredictions.drawDate,
              numbers: importPredictions.numbers[gameIdx],
              powerball: importPredictions.powerball ? importPredictions.powerball[gameIdx] : null,
              ticket_cost: parseFloat(importCost)
            },
            {
              withCredentials: true,
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          addedCount++;
        } catch (error) {
          if (error.response?.data?.detail === "DUPLICATE_TICKET") {
            duplicateCount++;
          }
        }
      }
      
      if (addedCount > 0 && duplicateCount > 0) {
        toast.success(`${addedCount} game(s) added! ${duplicateCount} duplicate(s) skipped.`);
      } else if (addedCount > 0) {
        toast.success(`${addedCount} game(s) added!`);
      } else if (duplicateCount > 0) {
        toast.error(`All ${duplicateCount} game(s) already exist in your Games Tracker.`);
      }
      
      setPredictionDialogOpen(false);
      setImportPredictions(null);
      setSelectedGames([]);
      fetchTickets();
    } catch (error) {
      toast.error("Failed to add games");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTicket = async (ticketId) => {
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Game deleted");
      fetchTickets();
    } catch (error) {
      toast.error("Failed to delete game");
    }
  };

  // Group delete confirmation state
  const [groupToDelete, setGroupToDelete] = useState(null); // { lotteryKey, dateKey, items }
  const [deletingGroup, setDeletingGroup] = useState(false);

  const requestDeleteDateGroup = (lotteryKey, dateKey, items) => {
    setGroupToDelete({ lotteryKey, dateKey, items });
  };

  const confirmDeleteDateGroup = async () => {
    if (!groupToDelete) return;
    setDeletingGroup(true);
    let deletedCount = 0;
    try {
      await Promise.all(groupToDelete.items.map(async (t) => {
        try {
          await axios.delete(`${API}/tickets/${t.ticket_id}`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          deletedCount++;
        } catch { /* ignore individual failures */ }
      }));
      if (deletedCount > 0) {
        toast.success(`${deletedCount} game${deletedCount !== 1 ? 's' : ''} deleted`);
      } else {
        toast.error("Failed to delete games");
      }
      setGroupToDelete(null);
      fetchTickets();
    } finally {
      setDeletingGroup(false);
    }
  };

  const toggleGameSelection = (index) => {
    setSelectedGames(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const totalSpend = tickets.reduce((sum, t) => sum + (t.ticket_cost || 0), 0);
  const totalWinnings = tickets.reduce((sum, t) => sum + (t.winnings || 0), 0);
  const netProfit = totalWinnings - totalSpend;
  const roi = totalSpend > 0 ? ((totalWinnings - totalSpend) / totalSpend * 100) : 0;

  const selectedLottery = lotteryTypes.find(l => l.id === formData.lottery_type);
  const hasPowerball = selectedLottery?.id === "powerball";

  const exportReport = () => {
    const content = `
AI Manifest - Games Tracking Report
Generated: ${new Date().toLocaleString()}
User: ${user?.email}

=== SUMMARY ===
Total Games: ${tickets.length}
Total Spend: $${totalSpend.toFixed(2)} AUD
Total Winnings: $${totalWinnings.toFixed(2)} AUD
Net Profit/Loss: $${netProfit.toFixed(2)} AUD
ROI: ${roi.toFixed(1)}%

=== GAMES ===
${tickets.map(t => `
${t.lottery_type.replace('_', ' ').toUpperCase()} - ${t.draw_date}
Numbers: ${t.numbers?.join(', ')}${t.powerball ? ` | PB: ${t.powerball}` : ''}
Cost: $${t.ticket_cost?.toFixed(2)} | Winnings: $${(t.winnings || 0).toFixed(2)}
${t.matches ? `Matches: ${t.matches}` : ''}
---`).join('\n')}

This report was generated by AI Manifest - AURA
Always gamble responsibly.
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aura_games_report_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  return (
    <DashboardLayout currentPage="tickets">
      <div className="p-4 sm:p-6 max-w-6xl w-full mx-auto overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Games Tracking</h1>
            <p className="text-aura-text-secondary">Track your lottery games and view results</p>
          </div>
          <div className="flex gap-3">
            {tickets.length > 0 && (
              <Button 
                variant="outline" 
                className="border-white/10 text-aura-text-secondary hover:text-white"
                onClick={exportReport}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-ticket-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Game
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Add New Game</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-aura-text-secondary">Lottery Type</Label>
                    <Select 
                      value={formData.lottery_type} 
                      onValueChange={(v) => setFormData({ ...formData, lottery_type: v })}
                    >
                      <SelectTrigger className="input-aura mt-1" data-testid="ticket-lottery-select">
                        <SelectValue placeholder="Select lottery" />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        {lotteryTypes.map((type) => (
                          <SelectItem 
                            key={type.id} 
                            value={type.id}
                            className="text-white hover:bg-white/10"
                          >
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-aura-text-secondary">Draw Date</Label>
                    <Input
                      type="date"
                      value={formData.draw_date}
                      onChange={(e) => setFormData({ ...formData, draw_date: e.target.value })}
                      className="input-aura mt-1"
                      required
                      data-testid="ticket-date-input"
                    />
                  </div>

                  <div>
                    <Label className="text-aura-text-secondary">Numbers (space or comma separated)</Label>
                    <Input
                      type="text"
                      value={formData.numbers}
                      onChange={(e) => setFormData({ ...formData, numbers: e.target.value })}
                      placeholder="e.g., 5 12 23 34 41 45"
                      className="input-aura mt-1"
                      required
                      data-testid="ticket-numbers-input"
                    />
                  </div>

                  {hasPowerball && (
                    <div>
                      <Label className="text-aura-text-secondary">Powerball Number</Label>
                      <Input
                        type="number"
                        value={formData.powerball}
                        onChange={(e) => setFormData({ ...formData, powerball: e.target.value })}
                        placeholder="e.g., 7"
                        className="input-aura mt-1"
                        min={1}
                        max={20}
                        data-testid="ticket-powerball-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label className="text-aura-text-secondary">Game Cost ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.ticket_cost}
                      onChange={(e) => setFormData({ ...formData, ticket_cost: e.target.value })}
                      placeholder="e.g., 1.45"
                      className="input-aura mt-1"
                      required
                      data-testid="ticket-cost-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={submitting}
                    data-testid="ticket-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Add Game"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Import from Predictions Dialog */}
        <Dialog open={predictionDialogOpen} onOpenChange={setPredictionDialogOpen}>
          <DialogContent className="bg-aura-surface border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add Predictions to Games</DialogTitle>
            </DialogHeader>
            {importPredictions && (
              <div className="space-y-4 mt-4">
                <div className="text-sm text-aura-text-secondary">
                  <span className="capitalize">{importPredictions.lotteryType?.replace('_', ' ')}</span>
                  {' • '}
                  <span>{formatDate(importPredictions.drawDate)}</span>
                </div>

                <div>
                  <Label className="text-aura-text-secondary mb-2 block">Cost per Game ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={importCost}
                    onChange={(e) => setImportCost(e.target.value)}
                    className="input-aura"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-aura-text-secondary">Select games to add:</Label>
                  {importPredictions.numbers?.map((nums, index) => (
                    <div 
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedGames.includes(index) 
                          ? 'bg-aura-emerald/10 border-aura-emerald/30' 
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                      onClick={() => toggleGameSelection(index)}
                    >
                      <Checkbox 
                        checked={selectedGames.includes(index)}
                        className="border-white/30 data-[state=checked]:bg-aura-emerald data-[state=checked]:border-aura-emerald"
                      />
                      <span className="text-xs text-aura-text-muted w-16">Game {index + 1}</span>
                      <div className="flex gap-1 flex-wrap">
                        {nums.map((num, i) => (
                          <span key={i} className="lottery-ball small">{num}</span>
                        ))}
                        {importPredictions.powerball && (
                          <span className="lottery-ball powerball small ml-1">
                            {importPredictions.powerball[index]}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                    onClick={() => handleAddPredictions(false)}
                    disabled={submitting || selectedGames.length === 0}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Add Selected ({selectedGames.length})</>
                    )}
                  </Button>
                  <Button 
                    className="flex-1 btn-gold"
                    onClick={() => handleAddPredictions(true)}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Add All ({importPredictions.numbers?.length})</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-aura p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-aura-emerald/10 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-aura-emerald" />
              </div>
              <div>
                <p className="text-aura-text-muted text-sm">Total Games</p>
                <p className="font-heading text-2xl font-bold text-white">{tickets.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-aura p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-aura-text-muted text-sm">Total Spend</p>
                <p className="font-heading text-2xl font-bold text-white">${totalSpend.toFixed(2)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-aura p-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-aura-gold/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-aura-gold" />
              </div>
              <div>
                <p className="text-aura-text-muted text-sm">Total Winnings</p>
                <p className="font-heading text-2xl font-bold text-aura-gold">${totalWinnings.toFixed(2)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-aura p-6"
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${roi >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <FileText className={`w-6 h-6 ${roi >= 0 ? 'text-green-400' : 'text-red-400'}`} />
              </div>
              <div>
                <p className="text-aura-text-muted text-sm">ROI</p>
                <p className={`font-heading text-2xl font-bold ${roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-aura-emerald mx-auto" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 card-aura">
            <Ticket className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No games yet</h3>
            <p className="text-aura-text-secondary mb-4">Start tracking your lottery games</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Manually
              </Button>
              <Button 
                variant="outline" 
                className="border-aura-emerald/20 text-aura-emerald hover:bg-aura-emerald/10"
                onClick={() => navigate("/predictions")}
              >
                Generate Predictions First
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const lotteryGroups = {
                oz_lotto: { label: "Oz Lotto", items: [] },
                tatts_lotto: { label: "Tatts Lotto", items: [] },
                weekday_windfall: { label: "Weekday Windfall", items: [] },
                powerball: { label: "Powerball", items: [] },
              };
              tickets.forEach(t => {
                const group = lotteryGroups[t.lottery_type];
                if (group) group.items.push(t);
                else {
                  if (!lotteryGroups["other"]) lotteryGroups["other"] = { label: "Other", items: [] };
                  lotteryGroups["other"].items.push(t);
                }
              });
              return Object.entries(lotteryGroups)
                .filter(([, g]) => g.items.length > 0)
                .map(([key, group]) => (
                  <LotteryAccordion key={key} lotteryKey={key} label={group.label} items={group.items} deleteTicket={deleteTicket} deleteDateGroup={requestDeleteDateGroup} />
                ));
            })()}
          </div>
        )}

        {/* Match Color Legend */}
        {tickets.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-6 py-3 px-5 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="match-color-legend">
            <span className="text-xs text-aura-text-muted uppercase tracking-wider mr-1">Match Key</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#10B981', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
              <span className="text-xs text-aura-text-secondary">Main Number</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.6)' }} />
              <span className="text-xs text-aura-text-secondary">Supplementary</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ef4444', boxShadow: '0 0 6px rgba(239,68,68,0.6)' }} />
              <span className="text-xs text-aura-text-secondary">Powerball</span>
            </span>
          </div>
        )}

        {/* Responsible Gambling Note */}
        <div className="mt-8 p-4 rounded-xl bg-red-500/5 border border-red-500/20">
          <p className="text-sm text-red-400 text-center">
            Remember to gamble responsibly. Set spending limits in Settings to track your activity.
          </p>
        </div>

        {/* Delete Date Group Confirmation Dialog */}
        <Dialog open={!!groupToDelete} onOpenChange={(o) => { if (!o && !deletingGroup) setGroupToDelete(null); }}>
          <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md" data-testid="delete-group-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Delete Date Group?</DialogTitle>
            </DialogHeader>
            {groupToDelete && (
              <div className="space-y-4 mt-2">
                <p className="text-sm text-aura-text-secondary">
                  This will permanently delete <span className="text-white font-medium">{groupToDelete.items.length} game{groupToDelete.items.length !== 1 ? 's' : ''}</span> for{' '}
                  <span className="text-white font-medium capitalize">{(groupToDelete.lotteryKey || '').replace('_', ' ')}</span> on{' '}
                  <span className="text-white font-medium">{formatDate(groupToDelete.dateKey)}</span>.
                </p>
                <p className="text-xs text-red-400">This action cannot be undone.</p>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                    onClick={() => setGroupToDelete(null)}
                    disabled={deletingGroup}
                    data-testid="delete-group-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                    onClick={confirmDeleteDateGroup}
                    disabled={deletingGroup}
                    data-testid="delete-group-confirm-btn"
                  >
                    {deletingGroup ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" />Delete {groupToDelete.items.length}</>}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TicketsPage;
